export const WHATSAPP_TEMPLATE_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  PAUSED: 'paused',
  DISABLED: 'disabled',
} as const

export type WhatsappTemplateStatus =
  (typeof WHATSAPP_TEMPLATE_STATUS)[keyof typeof WHATSAPP_TEMPLATE_STATUS]

export const WHATSAPP_TEMPLATE_SIGNATURE_MODE = {
  NONE: 'none',
  SENDER_NAME: 'sender_name',
} as const

export const WHATSAPP_TEMPLATE_VARIABLE_SOURCE = {
  SENDER_NAME: 'sender_name',
  CONTACT_FIRST_NAME: 'contact_first_name',
  CLIENT_COMPANY_NAME: 'client_company_name',
  MANUAL: 'manual',
} as const

export type WhatsappTemplateVariableSource =
  (typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCE)[keyof typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCE]

export const WHATSAPP_TEMPLATE_HEADER_FORMAT = {
  NONE: 'NONE',
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  DOCUMENT: 'DOCUMENT',
} as const

export type WhatsappTemplateHeaderFormat =
  (typeof WHATSAPP_TEMPLATE_HEADER_FORMAT)[keyof typeof WHATSAPP_TEMPLATE_HEADER_FORMAT]

export const WHATSAPP_BILLING_STATUS = {
  UNVERIFIED: 'unverified',
  PAYMENT_PENDING: 'payment_pending',
  VERIFICATION_PENDING: 'verification_pending',
  READY: 'ready',
} as const

export const WHATSAPP_BILLING_STATUS_CHANGED_EVENT =
  'whatsapp.billing.statusChanged'

export type WhatsappBillingState =
  (typeof WHATSAPP_BILLING_STATUS)[keyof typeof WHATSAPP_BILLING_STATUS]

export interface WhatsappBillingStatus {
  status: WhatsappBillingState
  failureCode: number | null
  failureDetectedAt: string | null
  confirmedAt: string | null
  resolutionUrl: string | null
}

export type WhatsappTemplateCategory =
  | 'MARKETING'
  | 'UTILITY'
  | 'AUTHENTICATION'

export const WHATSAPP_BROADCAST_TEMPLATE_CATEGORIES: readonly WhatsappTemplateCategory[] =
  ['MARKETING', 'UTILITY']

export interface WhatsappTemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS'
  format?: string
  text?: string
  example?: Record<string, unknown>
  buttons?: Array<Record<string, unknown>>
  add_security_recommendation?: boolean
  code_expiration_minutes?: number
}

export interface WhatsappTemplateConfig {
  signature?: {
    mode: (typeof WHATSAPP_TEMPLATE_SIGNATURE_MODE)[keyof typeof WHATSAPP_TEMPLATE_SIGNATURE_MODE]
    variablePosition?: number
  }
  variableBindings?: Record<
    string,
    {
      source: WhatsappTemplateVariableSource
      editorToken: string
    }
  >
  headerMedia?: {
    format: WhatsappTemplateHeaderFormat
    path: string
    fileName: string
    mimeType: string
    url?: string
  }
}

export interface WhatsappTemplate {
  id: number
  companyId: number
  whatsappIntegrationId: number
  name: string
  languageCode: string
  category: WhatsappTemplateCategory
  status: WhatsappTemplateStatus
  providerTemplateId?: string | null
  qualityRating?: string | null
  rejectionReason?: string | null
  components: WhatsappTemplateComponent[]
  sampleVariables?: Record<string, string> | null
  config?: WhatsappTemplateConfig | null
  purpose?: WhatsappTemplatePurpose | null
  raw?: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
  submittedAt?: string | null
  lastSyncedAt?: string | null
  providerDeletedAt?: string | null
}

export interface CreateWhatsappTemplateInput {
  purpose?: WhatsappTemplatePurpose
  name: string
  category: WhatsappTemplateCategory
  languageCode?: string
  components: WhatsappTemplateComponent[]
  sampleVariables?: Record<string, string>
  config?: WhatsappTemplateConfig
}

export type WhatsappTemplatePurpose =
  | 'conversation_start'
  | 'contact_birthday'
  | 'client_entry_anniversary'
  | 'client_opening_anniversary'
  | 'client_unified_anniversary'
  | 'conversation_finish'
  | 'csat'

export type UpdateWhatsappTemplateInput = Partial<CreateWhatsappTemplateInput>

export interface WhatsappTemplateMediaUpload {
  id?: string
  handle?: string
  path: string
  url: string
  fileName: string
  mimeType: string
}

export interface WhatsappTemplateExecutionValue {
  templateId: string
  variables: Record<string, string>
  media?: Pick<
    WhatsappTemplateMediaUpload,
    'path' | 'fileName' | 'mimeType'
  >
}

export interface WhatsappTemplateHistory {
  id: number
  templateId: number | null
  eventKind: string
  previousStatus?: string | null
  nextStatus?: string | null
  details?: Record<string, unknown> | null
  createdAt: string
}

export interface CoexSignupInput {
  code: string
  phoneNumberId: string
  wabaId: string
  businessId: string
  linkedPageId?: string
  linkedPageName?: string
  linkedPixelId?: string
  linkedPixelName?: string
}
